import React, { useState, createContext, useContext, useEffect } from 'react';
import IconSDK, { IconBuilder, IconConverter, HttpProvider } from 'icon-sdk-js';

import { ICONEX_RELAY } from '../utils/constants';
import {
  convertHexToDecimal,
  convertIcxToLoop,
  round,
} from '../utils/Conversions';

import { getNetwork, NETWORK_REF_MAINNET } from '../utils/network';
import { convertLoopToIcx } from '../utils/Conversions';

const NETWORK_REF = NETWORK_REF_MAINNET;
const ICX_TOKEN_CONTRACT = 'cx0000000000000000000000000000000000000000';
const SCORE_ADDRESS = 'cx6f824b1ccc08c13f626b8fc888d8214632cba28a';

const INITIAL_STATE = {
  network: getNetwork(NETWORK_REF),
  marketInfo: [],
  tokens: {},
  tokenInfo: [],
  getLatestTransactions: null,
  getMarketBuyersPendingSwaps: null,
  getMarketSellersPendingSwaps: null,
  handleLogin: null,
  address: null,
  logout: null,
  getICXBalance: null,
  getTokenBalance: null,
  createSwap: null,
  cancelSwap: null,
  getLastPrice: null,
  fillOrder: null,
  getAccountPendingSwaps: null,
  getSwap: null,
  getCompletedSwaps: null,
};

const INITIAL_ICON_PROVIDER = new HttpProvider(
  INITIAL_STATE.network.apiEndpoint
);

const DEBUG_ICON_PROVIDER = new HttpProvider(
  `https://ctz.solidwallet.io/api/debug/v3`
);

const swapTypes = {
  AC3: 'cx9ab3078e72c8d9017194d17b34b1a47b661945ca',
  SPORTS: 'cx08eb683a53d6f244d538a45f0cc584825e2cb985',
  TAP: 'cxc0b5b52c9f8b4251a47e91dda3bd61e5512cd782',
  WOK: 'cx921205acb7c51e16d5b7cbc37539a4357d929d20',
  ICX: 'cx0000000000000000000000000000000000000000',
};

export const IconSwapServiceContext = createContext(INITIAL_STATE);

export const useIconSwapService = () => {
  return useContext(IconSwapServiceContext);
};

export const IconSwapService = ({ children }) => {
  const [iconService, setIconService] = useState(
    new IconSDK(INITIAL_ICON_PROVIDER)
  );

  const _getDebugIconService = () => {
    return new IconSDK(DEBUG_ICON_PROVIDER);
  };

  const [address, setAddress] = useState(INITIAL_STATE.isAuthenticated);
  const [marketInfo, setMarketInfo] = useState();
  const [tokens, setTokens] = useState();
  const [tokenInfo, setTokenInfo] = useState();

  useEffect(() => {
    getMarketInfo();
    getTokenInfo();
    const localStorageValue = localStorage.getItem('address');
    if (localStorageValue) {
      setAddress(localStorageValue);
    }
  }, []);

  const getMarketInfo = async () => {
    const tokenExchanges = [];
    const builder = new IconBuilder.CallBuilder();
    const getMarketInfoCall = builder
      .to(SCORE_ADDRESS)
      .method('get_market_info')
      .params({ offset: '0x0' })
      .build();
    const marketInfoCall = await iconService.call(getMarketInfoCall).execute();
    marketInfoCall.pairs.map((data) => {
      const splitData = data.name.split('/');
      if (splitData[0] !== 'cx0000000000000000000000000000000000000000') {
        return;
      }
      if (splitData[1] === 'cx429731644462ebcfd22185df38727273f16f9b87') {
        return;
      }
      const tokensArray = Object.entries(marketInfoCall.tokens);
      return tokensArray.map((token) => {
        if (token[0] === splitData[1]) {
          data.name = token[1].name;
          return tokenExchanges.push(data);
        }
      });
    });
    setMarketInfo(tokenExchanges);
    setTokens(marketInfoCall.tokens);
    return;
  };

  const getTokenInfo = () => {
    fetch('https://faucet.reliantnode.com/24hr-swaps')
      .then((res) => res.json())
      .then((data) => setTokenInfo(data));
  };

  const getLatestTransactions = async (token) => {
    const builder = new IconBuilder.CallBuilder();
    const getTransactions = builder
      .to(SCORE_ADDRESS)
      .method('get_market_filled_swaps')
      .params({
        pair: `cx0000000000000000000000000000000000000000/${token}`,
        offset: '0x0',
      })
      .build();
    const transactions = await iconService.call(getTransactions).execute();
    return transactions.splice(0, 25);
  };

  const getTokenBalance = async (token) => {
    const builder = new IconBuilder.CallBuilder();
    const getBalance = builder
      .to(swapTypes[token])
      .method('balanceOf')
      .params({
        _owner: address,
      })
      .build();
    const balance = await iconService.call(getBalance).execute();
    const convertedBalance = convertLoopToIcx(balance);
    return convertedBalance;
  };

  const getICXBalance = async (address) => {
    const balanceInLoops = await iconService.getBalance(address).execute();
    return convertLoopToIcx(balanceInLoops);
  };

  const getSwap = async (swap_id) => {
    const stringToNumber = parseInt(swap_id);
    const hexValue = IconConverter.toHex(stringToNumber);
    const builder = new IconBuilder.CallBuilder();
    const getSwap = builder
      .to(SCORE_ADDRESS)
      .method('get_swap')
      .params({
        swap_id: hexValue,
      })
      .build();

    const getSingleSwap = await iconService.call(getSwap).execute();
    return getSingleSwap;
  };

  const getCompletedSwaps = async () => {
    const builder = new IconBuilder.CallBuilder();
    const getSwaps = builder
      .to(SCORE_ADDRESS)
      .method('get_account_filled_swaps')
      .params({
        address: address,
        offset: '0x0',
      })
      .build();

    const getAllSwaps = await iconService.call(getSwaps).execute();
    return getAllSwaps;
  };

  const getAccountPendingSwaps = async () => {
    const builder = new IconBuilder.CallBuilder();
    const getSwaps = builder
      .to(SCORE_ADDRESS)
      .method('get_account_pending_swaps')
      .params({ address: address, offset: '0x0' })
      .build();
    const pendingSwaps = await iconService.call(getSwaps).execute();
    return pendingSwaps;
  };

  const getMarketBuyersPendingSwaps = async (maker, taker) => {
    const builder = new IconBuilder.CallBuilder();
    const getSwaps = builder
      .to(SCORE_ADDRESS)
      .method('get_market_buyers_pending_swaps')
      .params({
        pair: `${maker}/${taker}`,
        offset: '0x0',
      })
      .build();
    const pendingSwaps = await iconService.call(getSwaps).execute();
    return pendingSwaps;
  };

  const getMarketSellersPendingSwaps = async (maker, taker) => {
    const builder = new IconBuilder.CallBuilder();
    const getSwaps = builder
      .to(SCORE_ADDRESS)
      .method('get_market_sellers_pending_swaps')
      .params({
        pair: `${maker}/${taker}`,
        offset: '0x0',
      })
      .build();
    const pendingSwaps = await iconService.call(getSwaps).execute();
    return pendingSwaps;
  };

  const getLastPrice = async (maker, taker) => {
    const builder = new IconBuilder.CallBuilder();
    const getLast = builder
      .to(SCORE_ADDRESS)
      .method('get_market_last_filled_swap')
      .params({
        pair: `${swapTypes[maker]}/${swapTypes[taker]}`,
      })
      .build();

    const lastPriceObject = await iconService.call(getLast).execute();
    if (
      Object.keys(lastPriceObject).length === 0 &&
      lastPriceObject.constructor === Object
    )
      return;
    if (
      lastPriceObject.maker.contract ===
      'cx0000000000000000000000000000000000000000'
    ) {
      const price =
        convertHexToDecimal(lastPriceObject.maker.amount) /
        convertHexToDecimal(lastPriceObject.taker.amount);
      const roundedPrice = round(price, 5);
      return roundedPrice;
    }
    if (
      lastPriceObject.taker.contract ===
      'cx0000000000000000000000000000000000000000'
    ) {
      const price =
        convertHexToDecimal(lastPriceObject.taker.amount) /
        convertHexToDecimal(lastPriceObject.maker.amount);
      const roundedPrice = round(price, 5);
      return roundedPrice;
    }
  };

  const handleLogin = () => {
    window.addEventListener(ICONEX_RELAY.RESPONSE, relayEventHandler, {
      once: true,
    });
    window.dispatchEvent(
      new CustomEvent(ICONEX_RELAY.REQUEST, {
        detail: { type: 'REQUEST_ADDRESS' },
      })
    );

    function relayEventHandler(event) {
      const { type, payload } = event.detail;
      switch (type) {
        case 'RESPONSE_ADDRESS':
          setAddress(payload);
          localStorage.setItem('address', payload);
          return;
        case 'RESPONSE_SIGNING':
          return console.log('HANDLE RESPONSE');
        case 'CANCEL':
        case 'CANCEL_SIGNING':
          return console.log('Cancel signing');
        default:
          console.warn(`No handler setup for event ${type}`);
      }
    }
  };

  const logout = () => {
    setAddress(null);
    localStorage.removeItem('address');
  };

  const cancelSwap = (swapIdNumber) => {
    const swapId = swapIdNumber.toString(16);
    return __iconexCallTransaction(address, SCORE_ADDRESS, 'cancel_swap', 0, {
      swap_id: swapId,
    }).then((txHash) => {
      return txHash;
    });
  };

  const fillOrder = (swapId, taker_contract, taker_amount) => {
    swapId = IconConverter.toHex(IconConverter.toBigNumber(swapId));
    if (taker_contract === ICX_TOKEN_CONTRACT) {
      const value = IconConverter.toHex(taker_amount);
      return __iconexCallTransaction(
        address,
        SCORE_ADDRESS,
        'fill_icx_order',
        value,
        { swap_id: swapId }
      ).then((tx) => {
        return tx;
      });
    } else {
      const value = IconConverter.toHex(taker_amount);
      const data = {
        action: 'fill_irc2_order',
        swap_id: swapId,
      };
      const params = {
        _to: SCORE_ADDRESS,
        _value: value,
        _data: IconConverter.toHex(JSON.stringify(data)),
      };
      return __iconexCallTransaction(
        address,
        taker_contract,
        'transfer',
        0,
        params
      ).then((tx) => {
        return tx;
      });
    }
  };

  const createSwap = (
    maker_contract,
    maker,
    taker_contract,
    taker,
    taker_address
  ) => {
    const maker_amount = IconConverter.toHex(convertIcxToLoop(maker));
    const taker_amount = IconConverter.toHex(convertIcxToLoop(taker));
    const SwapCreatedEvent = 'SwapCreatedEvent(int,int,int)';
    const getSwapIdFromTx = async (tx) => {
      if (!tx) return null;
      const txHash = tx['result'];
      const txResult = await __txResult(txHash);
      const eventLogs = txResult['eventLogs'][2];
      if (eventLogs['indexed'][0] !== SwapCreatedEvent) {
        console.log('EVEBNT LOGS', eventLogs);
        // throw WrongEventSignature(eventLogs['indexed']);
      }
      const swapId = parseInt(eventLogs['indexed'][1], 16);
      const maker = parseInt(eventLogs['data'][0], 16);
      const taker = parseInt(eventLogs['data'][1], 16);
      return { swapId: swapId, maker: maker, taker: taker };
    };

    if (swapTypes[maker_contract.value] === ICX_TOKEN_CONTRACT) {
      let params = {
        taker_contract: swapTypes[taker_contract.value],
        taker_amount: IconConverter.toHex(
          IconConverter.toBigNumber(taker_amount)
        ),
      };
      if (taker_address) {
        params['taker_address'] = taker_address;
      }
      return __iconexCallTransaction(
        address,
        SCORE_ADDRESS,
        'create_icx_swap',
        maker_amount,
        params
      ).then(async (tx) => {
        return getSwapIdFromTx(tx);
      });
    } else {
      let data = {
        action: 'create_irc2_swap',
        taker_contract: swapTypes[taker_contract.value],
        taker_amount: IconConverter.toHex(
          IconConverter.toBigNumber(taker_amount)
        ),
      };

      if (taker_address) {
        data['taker_address'] = taker_address;
      }
      const params = {
        _to: 'cx6f824b1ccc08c13f626b8fc888d8214632cba28a',
        _value: maker_amount,
        _data: IconConverter.toHex(JSON.stringify(data)),
      };
      return __iconexCallTransaction(
        address,
        swapTypes[maker_contract.value],
        'transfer',
        0,
        params
      ).then(async (tx) => {
        return getSwapIdFromTx(tx);
      });
    }
  };

  const __iconexCallTransaction = (from, to, method, value, params) => {
    return __estimateCallStep(from, to, method, value, params).then(
      (stepLimit) => {
        return __iconexCallTransactionEx(
          from,
          to,
          method,
          value,
          stepLimit,
          params
        );
      }
    );
  };

  const __estimateCallStep = (from, to, method, value, params = {}) => {
    const transaction = {
      jsonrpc: '2.0',
      method: 'debug_estimateStep',
      id: 1,
      params: {
        version: '0x3',
        from: from,
        to: to,
        value: IconConverter.toHex(IconConverter.toBigNumber(value)),
        timestamp: IconConverter.toHex(new Date().getTime() * 1000),
        nid: IconConverter.toHex(IconConverter.toBigNumber(1)),
        nonce: '0x1',
        dataType: 'call',
        data: {
          method: method,
          params: params,
        },
      },
    };
    return new Promise((resolve, reject) => {
      try {
        const result = _getDebugIconService()
          .provider.request(transaction)
          .execute();
        resolve(result);
      } catch (err) {
        reject(err);
      }
    });
  };

  const __iconexCallTransactionEx = (
    from,
    to,
    method,
    value,
    stepLimit,
    params
  ) => {
    const transaction = __icxCallTransactionBuild(
      from,
      to,
      method,
      value,
      stepLimit,
      params
    );
    const jsonRpcQuery = {
      jsonrpc: '2.0',
      method: 'icx_sendTransaction',
      params: IconConverter.toRawTransaction(transaction),
      id: 1234,
    };
    return __iconexJsonRpc(jsonRpcQuery);
  };

  const __iconexJsonRpc = (jsonRpcQuery) => {
    return __iconexConnectRequest('REQUEST_JSON-RPC', jsonRpcQuery);
  };

  const __iconexConnectRequest = (requestType, payload) => {
    return new Promise((resolve, reject) => {
      function eventHandler(event) {
        const { payload } = event.detail;
        window.removeEventListener('ICONEX_RELAY_RESPONSE', eventHandler);
        resolve(payload);
      }
      window.addEventListener('ICONEX_RELAY_RESPONSE', eventHandler);

      window.dispatchEvent(
        new window.CustomEvent('ICONEX_RELAY_REQUEST', {
          detail: {
            type: requestType,
            payload,
          },
        })
      );
    });
  };

  const __icxCallTransactionBuild = (
    from,
    to,
    method,
    value,
    stepLimit,
    params = {}
  ) => {
    let callTransactionBuilder = new IconBuilder.CallTransactionBuilder()
      .from(from)
      .to(to)
      .value(IconConverter.toHex(value))
      .stepLimit(IconConverter.toBigNumber(stepLimit))
      .nid(IconConverter.toBigNumber(1))
      .nonce(IconConverter.toBigNumber(1))
      .version(IconConverter.toBigNumber(3))
      .timestamp(new Date().getTime() * 1000)
      .method(method);

    // Optional "params" field
    if (Object.keys(params).length !== 0) {
      callTransactionBuilder = callTransactionBuilder.params(params);
    }

    return callTransactionBuilder.build();
  };

  const __txResult = async (txHash, retriesLeft = 1000, interval = 100) => {
    try {
      return await iconService.getTransactionResult(txHash).execute();
    } catch (error) {
      if (retriesLeft) {
        await new Promise((resolve, reject) => setTimeout(resolve, interval));
        return __txResult(txHash, retriesLeft - 1, interval);
      } else {
        throw new Error(txHash);
      }
    }
  };

  return (
    <IconSwapServiceContext.Provider
      value={{
        marketInfo,
        tokens,
        tokenInfo,
        getLatestTransactions,
        getMarketBuyersPendingSwaps,
        getMarketSellersPendingSwaps,
        handleLogin,
        address,
        logout,
        getICXBalance,
        getTokenBalance,
        createSwap,
        cancelSwap,
        getLastPrice,
        fillOrder,
        getAccountPendingSwaps,
        getSwap,
        getCompletedSwaps,
      }}
    >
      {children}
    </IconSwapServiceContext.Provider>
  );
};

export default IconSwapService;
